/**
 * Utility Functions
 */

import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Merge class names with Tailwind CSS classes
 * Combines clsx and tailwind-merge for optimal class merging
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/**
 * Fecha civil: un dia del calendario, sin hora ni huso.
 *
 * POR QUE HAY QUE DISTINGUIRLA DE UN INSTANTE
 * ===========================================
 * `new Date('1988-04-22')` no es "22 de abril": es la medianoche de ese dia
 * EN UTC. Al mostrarla en Colombia (UTC-5) el navegador retrocede cinco horas
 * y pinta el 21 de abril.
 *
 * En un sistema clinico eso no es cosmetico. La fecha de nacimiento
 * identifica al paciente, va en el informe, y ademas es la credencial con la
 * que entra a su portal: si el sistema la corre un dia, el paciente teclea la
 * suya de verdad y no puede ver sus resultados, sin ningun mensaje que lo
 * explique. Lo mismo con la fecha del estudio, que ordena la historia.
 *
 * Una fecha de nacimiento o de estudio es un dia del calendario, no un
 * instante: se muestra tal cual se guardo, sin convertir husos. Un
 * `createdAt`, en cambio, SI es un instante y debe convertirse.
 */
const PATRON_FECHA_CIVIL =
  /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.000)?(?:Z|\+00:00)?)?$/

const MESES_CORTOS = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
]

/** Partes de una fecha civil, o null si el valor es un instante. */
export function partesDeFechaCivil(
  valor: Date | string | null | undefined
): { anio: number; mes: number; dia: number } | null {
  if (typeof valor !== 'string') return null

  const coincidencia = PATRON_FECHA_CIVIL.exec(valor.trim())
  if (!coincidencia) return null

  return {
    anio: Number(coincidencia[1]),
    mes: Number(coincidencia[2]),
    dia: Number(coincidencia[3]),
  }
}

/**
 * Convierte una fecha civil en un `Date` cuyas partes LOCALES son las
 * correctas, para poder darle formato con `toLocaleDateString` sin que el
 * huso la corra un dia.
 *
 * Un instante se devuelve tal cual: ahi la conversion de huso SI corresponde.
 */
export function aFechaSegura(valor: Date | string): Date {
  const civil = partesDeFechaCivil(valor)
  if (civil) return new Date(civil.anio, civil.mes - 1, civil.dia)
  return typeof valor === 'string' ? new Date(valor) : valor
}

/**
 * Format date for display (Colombian format: DD/MM/YYYY)
 */
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '-'

  const civil = partesDeFechaCivil(date)
  if (civil) {
    const dd = String(civil.dia).padStart(2, '0')
    const mm = String(civil.mes).padStart(2, '0')
    return `${dd}/${mm}/${civil.anio}`
  }

  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

/**
 * Fecha civil en palabras: "22 de abr de 1988".
 *
 * Misma regla que formatDate: un dia del calendario no se convierte de huso.
 */
export function formatFechaLarga(date: Date | string | null | undefined): string {
  if (!date) return '-'

  const civil = partesDeFechaCivil(date)
  if (civil) {
    return `${civil.dia} de ${MESES_CORTOS[civil.mes - 1]} de ${civil.anio}`
  }

  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * Edad cumplida a partir de una fecha civil de nacimiento.
 */
export function calcularEdad(nacimiento: Date | string | null | undefined): number | null {
  if (!nacimiento) return null

  const civil = partesDeFechaCivil(nacimiento)
  const hoy = new Date()

  const anio = civil ? civil.anio : new Date(nacimiento as string).getFullYear()
  const mes = civil ? civil.mes : new Date(nacimiento as string).getMonth() + 1
  const dia = civil ? civil.dia : new Date(nacimiento as string).getDate()

  if (Number.isNaN(anio)) return null

  let edad = hoy.getFullYear() - anio
  const mesActual = hoy.getMonth() + 1
  if (mesActual < mes || (mesActual === mes && hoy.getDate() < dia)) edad -= 1

  return edad
}

/**
 * Format date and time for display
 */
export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) return '-'
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleString('es-CO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Format relative time (e.g., "hace 5 minutos")
 */
export function formatRelativeTime(date: Date | string | null | undefined): string {
  if (!date) return '-'
  const d = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) return 'hace un momento'
  if (diffMin < 60) return `hace ${diffMin} ${diffMin === 1 ? 'minuto' : 'minutos'}`
  if (diffHour < 24) return `hace ${diffHour} ${diffHour === 1 ? 'hora' : 'horas'}`
  if (diffDay < 7) return `hace ${diffDay} ${diffDay === 1 ? 'día' : 'días'}`
  return formatDate(d)
}

/**
 * Capitalize first letter of string
 */
export function capitalize(str: string): string {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

/**
 * Truncate string with ellipsis
 */
export function truncate(str: string, length: number): string {
  if (!str) return ''
  if (str.length <= length) return str
  return str.slice(0, length) + '...'
}

/**
 * Generate initials from name
 */
export function getInitials(name: string): string {
  if (!name) return ''
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * Sleep utility for async operations
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: Parameters<T>) => ReturnType<T>>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => {
      fn(...args)
    }, delay)
  }
}

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 */
export function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === 'object') return Object.keys(value).length === 0
  return false
}

/**
 * Generate a random ID
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2, 15)
}

/**
 * Parse query string to object
 */
export function parseQueryString(queryString: string): Record<string, string> {
  const params = new URLSearchParams(queryString)
  const result: Record<string, string> = {}
  params.forEach((value, key) => {
    result[key] = value
  })
  return result
}

/**
 * Build query string from object
 */
export function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value))
    }
  })
  return searchParams.toString()
}
